import React, { useEffect, useRef, useState } from "react";
import { Search, X, MapPin, Video as VideoIcon } from "lucide-react";
import api from "../../lib/apiClient";

/**
 * FeedSearchBar — inline channel + video search.
 *
 * Lives between the player controls and the Live Chat, per Eric's
 * Jun 9, 2026 request: "Users need a way to be able to search for
 * channel names or videos by name or title. Put a small search bar
 * above the Live Chat, underneath the screen filter pills."
 *
 * Behaviour:
 *   - 300ms debounce → GET /api/search?q=...
 *   - Dropdown panel below the input with two sections (Channels,
 *     Videos). Each row links to the destination and calls
 *     `handleClosePlayer` so the overlay dismisses cleanly on click.
 *   - Empty-state copy when q has < 2 chars; "no matches" when the
 *     query returned both arrays empty.
 *   - ESC clears; click-outside closes.
 */
const FeedSearchBar = ({ navigate, handleClosePlayer }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Click-outside collapse
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Debounced fetch
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
        setResults(res.data);
      } catch {
        setResults({ channels: [], videos: [] });
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const goTo = (path) => {
    setOpen(false);
    setQ("");
    setResults(null);
    handleClosePlayer && handleClosePlayer();
    navigate(path);
  };

  const total = (results?.channels?.length || 0) + (results?.videos?.length || 0);

  return (
    <div ref={wrapperRef} className="relative" data-testid="feed-search-bar">
      <div className="flex items-center gap-2 bg-gray-900/80 border border-gray-800 rounded-lg px-2.5 py-1.5 focus-within:border-emerald-700">
        <Search className="h-3.5 w-3.5 text-gray-500 shrink-0" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === "Escape") { setQ(""); setOpen(false); } }}
          placeholder="Search channels or videos…"
          className="flex-1 bg-transparent text-white placeholder-gray-500 text-xs focus:outline-none min-w-0"
          data-testid="feed-search-input"
        />
        {q && (
          <button
            onClick={() => { setQ(""); setResults(null); setOpen(false); }}
            className="text-gray-500 hover:text-gray-300 shrink-0"
            aria-label="Clear search"
            data-testid="feed-search-clear-btn"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute top-full left-0 right-0 mt-1 bg-gray-900 border border-gray-800 rounded-lg shadow-2xl z-30 max-h-80 overflow-y-auto"
          data-testid="feed-search-results"
        >
          {loading && (
            <div className="px-3 py-2.5 text-xs text-gray-500">Searching…</div>
          )}
          {!loading && results && total === 0 && (
            <div className="px-3 py-2.5 text-xs text-gray-500" data-testid="feed-search-empty">
              No channels or videos match <span className="text-gray-300">"{q.trim()}"</span>.
            </div>
          )}
          {!loading && results?.channels?.length > 0 && (
            <div className="border-b border-gray-800/80">
              <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Channels</p>
              {results.channels.map((c) => (
                <button
                  key={`chan-${c.id}`}
                  onClick={() => goTo(`/${c.username}`)}
                  className="w-full text-left px-3 py-1.5 hover:bg-gray-800/80 flex items-center gap-2 text-xs"
                  data-testid={`feed-search-channel-${c.username}`}
                >
                  <span className="text-emerald-400 font-medium truncate">@{c.username}</span>
                  {c.country && (
                    <span className="text-gray-500 text-[10px] flex items-center gap-0.5 shrink-0">
                      <MapPin className="h-2.5 w-2.5" /> {c.country}
                    </span>
                  )}
                  <span className="text-gray-600 text-[10px] ml-auto shrink-0">
                    {c.follower_count} follower{c.follower_count === 1 ? "" : "s"}
                  </span>
                </button>
              ))}
            </div>
          )}
          {!loading && results?.videos?.length > 0 && (
            <div>
              <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Videos</p>
              {results.videos.map((v) => (
                <button
                  key={`vid-${v.id}`}
                  onClick={() => goTo(`/${v.username}?v=${v.id}`)}
                  className="w-full text-left px-3 py-1.5 hover:bg-gray-800/80 flex items-start gap-2 text-xs"
                  data-testid={`feed-search-video-${v.id}`}
                >
                  <VideoIcon className="h-3 w-3 text-gray-500 mt-0.5 shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-white truncate">{v.title}</span>
                    <span className="block text-gray-500 text-[10px] truncate">
                      @{v.username}
                      {v.location?.city ? ` · ${v.location.city}` : ""}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FeedSearchBar;
